/**
 * Assetrac Users Page (Admin Only)
 * Manage all users across the system
 */

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Avatar,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  AdminPanelSettings as AdminIcon,
  Person as UserIcon,
  Search as SearchIcon,
} from '@mui/icons-material';

import AppBar from '../../elements/AppBar';
import PageBody from '../../elements/PageBody';
import PageTitle from '../../elements/PageTitle';
import FilterSection from '../../elements/FilterSection';
import useProjectSelector from '../../../hooks/useProjectSelector';

// Mock data - will be replaced with API call
const initialMockUsers = [
  { 
    id: '1', 
    email: 'admin@assetrac.com', 
    name: 'System Admin', 
    role: 'admin', 
    customer: 'Assetrac',
    status: 'active',
    lastLogin: '2026-01-21T10:30:00Z'
  },
  { 
    id: '2', 
    email: 'rvernon@northernclearing.com', 
    name: 'Richard Vernon', 
    role: 'customer_admin', 
    customer: 'Northern Clearing Inc.',
    status: 'active',
    lastLogin: '2026-01-20T15:45:00Z'
  },
  { 
    id: '3', 
    email: 'smitchell@greatplainsenergy.com', 
    name: 'Sarah Mitchell', 
    role: 'customer_admin', 
    customer: 'Great Plains Energy',
    status: 'active',
    lastLogin: '2026-01-21T08:00:00Z'
  },
  { 
    id: '4', 
    email: 'mrodriguez@transconpipeline.com', 
    name: 'Mike Rodriguez', 
    role: 'user', 
    customer: 'TransCon Pipeline Services',
    status: 'active',
    lastLogin: '2026-01-19T12:00:00Z'
  },
  { 
    id: '5', 
    email: 'demo@portica.ai', 
    name: 'Demo User', 
    role: 'admin', 
    customer: 'Assetrac',
    status: 'active',
    lastLogin: '2026-01-21T11:00:00Z'
  },
];

const AssetracUsersPage = () => {
  const { ProjectSelectorComponent } = useProjectSelector();
  const [searchQuery, setSearchQuery] = useState('');
  const [users, setUsers] = useState(initialMockUsers);
  
  // Modal states
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'user',
    customer: '',
    status: 'active',
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);

  const getRoleChip = (role) => {
    const config = {
      admin: { label: 'Admin', color: 'secondary', icon: <AdminIcon sx={{ fontSize: 14 }} /> },
      customer_admin: { label: 'Customer Admin', color: 'info', icon: <AdminIcon sx={{ fontSize: 14 }} /> },
      user: { label: 'User', color: 'default', icon: <UserIcon sx={{ fontSize: 14 }} /> },
    };
    const { label, color, icon } = config[role] || config.user;
    return <Chip label={label} size="small" color={color} icon={icon} />;
  };

  // Handle opening add user modal
  const handleOpenAddModal = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      role: 'user',
      customer: '',
      status: 'active',
    });
    setFormError(null);
    setUserModalOpen(true);
  };

  // Handle opening edit user modal
  const handleOpenEditModal = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      role: user.role,
      customer: user.customer,
      status: user.status,
    });
    setFormError(null);
    setUserModalOpen(true);
  };

  // Handle closing user modal
  const handleCloseModal = () => {
    setUserModalOpen(false);
    setEditingUser(null);
    setFormError(null);
  };

  // Handle form input changes
  const handleFormChange = (field) => (event) => {
    setFormData(prev => ({ ...prev, [field]: event.target.value }));
  };

  // Handle form submission
  const handleSubmitUser = async () => {
    setFormLoading(true);
    setFormError(null);

    // Validation
    if (!formData.name.trim()) {
      setFormError('Name is required');
      setFormLoading(false);
      return;
    }
    if (!formData.email.trim()) {
      setFormError('Email is required');
      setFormLoading(false);
      return;
    }

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      
      if (editingUser) {
        // Update existing user
        setUsers(prev => prev.map(u => 
          u.id === editingUser.id 
            ? { ...u, ...formData }
            : u
        ));
      } else {
        // Create new user
        const newUser = {
          id: String(Date.now()),
          ...formData,
          lastLogin: null,
        };
        setUsers(prev => [...prev, newUser]);
      }
      
      handleCloseModal();
    } catch (err) {
      setFormError(err.message || 'Failed to save user');
    } finally {
      setFormLoading(false);
    }
  };

  // Handle opening delete dialog
  const handleOpenDeleteDialog = (user) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  // Handle delete user
  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    setFormLoading(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      
      setUsers(prev => prev.filter(u => u.id !== userToDelete.id));
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    } catch (err) {
      console.error('Error deleting user:', err);
    } finally {
      setFormLoading(false);
    }
  };

  // Filter users by search query
  const filteredUsers = users.filter(user => {
    if (!searchQuery || !searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    const nameMatch = user.name && user.name.toLowerCase().includes(q);
    const emailMatch = user.email && user.email.toLowerCase().includes(q);
    const customerMatch = user.customer && user.customer.toLowerCase().includes(q);
    return nameMatch || emailMatch || customerMatch;
  });

  // Helper function to get button content
  const getSubmitButtonContent = () => {
    if (formLoading) {
      return <CircularProgress size={20} aria-label="Saving..." />;
    }
    return editingUser ? 'Save Changes' : 'Add User';
  };

  const getDeleteButtonContent = () => {
    if (formLoading) {
      return <CircularProgress size={20} aria-label="Deleting..." />;
    }
    return 'Delete';
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <AppBar
        showProjectSelector
        projectSelectorProps={{
          ProjectSelectorComponent,
        }}
      />
      
      <PageBody>
        {/* Title Row */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <PageTitle>
            All System Users
          </PageTitle>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleOpenAddModal}
            sx={{ height: 44 }}
          >
            Add User
          </Button>
        </Box>

        {/* Search Bar */}
        <FilterSection>
          <TextField
            placeholder="Search users..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            sx={{ width: 280 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />
        </FilterSection>

        <TableContainer component={Paper} sx={{ boxShadow: 'none', border: '1px solid #4A4847' }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Customer</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Login</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers.map((user) => (
                <TableRow key={user.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                        {user.name.charAt(0)}
                      </Avatar>
                      <Box>
                        <Typography variant="body2" fontWeight={500}>
                          {user.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {user.email}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {user.customer}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {getRoleChip(user.role)}
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={user.status} 
                      size="small"
                      color={user.status === 'active' ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    {user.lastLogin ? (
                      <>
                        <Typography variant="body2">
                          {new Date(user.lastLogin).toLocaleDateString()}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(user.lastLogin).toLocaleTimeString()}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Never
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton 
                      size="small" 
                      title="Edit user"
                      onClick={() => handleOpenEditModal(user)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton 
                      size="small" 
                      title="Delete user" 
                      color="error"
                      onClick={() => handleOpenDeleteDialog(user)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </PageBody>

      {/* Add/Edit User Modal */}
      <Dialog 
        open={userModalOpen} 
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          {formError && (
            <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
              {formError}
            </Alert>
          )}
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Name"
              value={formData.name}
              onChange={handleFormChange('name')}
              fullWidth
              required
            />
            <TextField
              label="Email"
              type="email"
              value={formData.email}
              onChange={handleFormChange('email')}
              fullWidth
              required
            />
            <TextField
              label="Customer"
              value={formData.customer}
              onChange={handleFormChange('customer')}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                label="Role"
                onChange={handleFormChange('role')}
              >
                <MenuItem value="user">User</MenuItem>
                <MenuItem value="customer_admin">Customer Admin</MenuItem>
                <MenuItem value="admin">Admin</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={formData.status}
                label="Status"
                onChange={handleFormChange('status')}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseModal} disabled={formLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitUser} 
            variant="contained"
            disabled={formLoading}
            aria-busy={formLoading}
          >
            {getSubmitButtonContent()}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete User</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete &quot;{userToDelete?.name}&quot;? 
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={formLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteUser} 
            color="error"
            variant="contained"
            disabled={formLoading}
            aria-busy={formLoading}
          >
            {getDeleteButtonContent()}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssetracUsersPage;
